import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InlineError } from "@/components/ui/inline-error";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { Loader2, Mail, AlertCircle, Clock } from "lucide-react";
import { useOnboarding } from "@/hooks/useOnboarding";
import { getAndClearReturnUrl } from "@/hooks/useSessionMonitor";

const signUpSchema = z.object({
  email: z.string().email({ message: "Email invalide" }),
  password: z.string().min(6, { message: "Le mot de passe doit contenir au moins 6 caractères" }),
  prenom: z.string().min(2, { message: "Le prénom doit contenir au moins 2 caractères" }),
  nom: z.string().min(2, { message: "Le nom doit contenir au moins 2 caractères" }),
  nomEntreprise: z.string().min(2, { message: "Le nom de l'entreprise est requis" }),
});

const signInSchema = z.object({
  email: z.string().email({ message: "Email invalide" }),
  password: z.string().min(6, { message: "Le mot de passe doit contenir au moins 6 caractères" }),
});

const Auth = () => {
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get("mode") === "signup" ? "signup" : "login";
  const [mode, setMode] = useState<"login" | "signup">(initialMode);
  const [loading, setLoading] = useState(false);
  const { signIn, signUp, signInWithGoogle, user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { checkOnboardingCompleted } = useOnboarding();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    prenom: "",
    nom: "",
    nomEntreprise: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [emailNotConfirmed, setEmailNotConfirmed] = useState<string | null>(null);
  const [loginFailed, setLoginFailed] = useState<string | null>(null);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState<string | null>(null);
  const [rateLimitError, setRateLimitError] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    const handlePostAuth = async () => {
      if (!user) return;

      // Vérifier si une entreprise était en attente (OAuth signup)
      const pendingEntreprise = localStorage.getItem('pendingEntreprise');
      if (pendingEntreprise) {
        localStorage.removeItem('pendingEntreprise');

        // Vérifier si l'utilisateur a déjà une entreprise
        const { data: existingEntreprise } = await supabase
          .from('entreprises')
          .select('id')
          .eq('proprietaire_user_id', user.id)
          .single();

        if (!existingEntreprise) {
          // Créer l'entreprise pour ce nouvel utilisateur Google
          const { error: companyError } = await supabase
            .from('entreprises')
            .insert({
              nom: pendingEntreprise,
              proprietaire_user_id: user.id,
            });

          if (companyError) {
            console.error("Error creating company:", companyError);
          } else {
            toast({
              title: "Bienvenue sur B8ild !",
              description: "Votre compte a été créé avec succès.",
            });
          }
        }
      }

      // Rediriger vers onboarding si pas complété
      if (!checkOnboardingCompleted()) {
        navigate("/onboarding");
      } else {
        // Vérifier s'il y a une URL de retour (après session expirée)
        const returnUrl = getAndClearReturnUrl();
        navigate(returnUrl || "/dashboard");
      }
    };

    handlePostAuth();
  }, [user, navigate, checkOnboardingCompleted, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      if (mode === "signup") {
        const validation = signUpSchema.safeParse(formData);
        if (!validation.success) {
          const fieldErrors: Record<string, string> = {};
          validation.error.issues.forEach((err) => {
            if (err.path[0]) {
              fieldErrors[err.path[0] as string] = err.message;
            }
          });
          setErrors(fieldErrors);
          setLoading(false);
          return;
        }

        const { error: signUpError } = await signUp(
          formData.email,
          formData.password,
          {
            prenom: formData.prenom,
            nom: formData.nom,
            entreprise: formData.nomEntreprise, // Sauvegardé pour création ultérieure si email confirmation requise
          }
        );

        if (signUpError) {
          if (signUpError.message.includes("already registered")) {
            toast({
              title: "Erreur",
              description: "Cet email est déjà utilisé. Connectez-vous ou utilisez un autre email.",
              variant: "destructive",
            });
          } else {
            toast({
              title: "Erreur lors de l'inscription",
              description: signUpError.message,
              variant: "destructive",
            });
          }
          setLoading(false);
          return;
        }

        // Vérifier si l'utilisateur est connecté après signup
        const { data: { user: newUser } } = await supabase.auth.getUser();

        if (newUser) {
          // Utilisateur connecté (pas de confirmation email requise)
          // Créer l'entreprise
          const { error: companyError } = await supabase
            .from('entreprises')
            .insert({
              nom: formData.nomEntreprise,
              proprietaire_user_id: newUser.id,
            });

          if (companyError) {
            console.error("Error creating company:", companyError);
          }

          toast({
            title: "Bienvenue sur B8ild !",
            description: "Découvrons ensemble comment optimiser vos chantiers.",
          });
          // Rediriger vers l'onboarding pour les nouveaux utilisateurs
          navigate("/onboarding");
        } else {
          // Email confirmation requise - afficher l'écran de confirmation
          setEmailNotConfirmed(formData.email);
          setLoading(false);
        }
      } else {
        const validation = signInSchema.safeParse(formData);
        if (!validation.success) {
          const fieldErrors: Record<string, string> = {};
          validation.error.issues.forEach((err) => {
            if (err.path[0]) {
              fieldErrors[err.path[0] as string] = err.message;
            }
          });
          setErrors(fieldErrors);
          setLoading(false);
          return;
        }

        const { error: signInError } = await signIn(formData.email, formData.password);

        if (signInError) {
          if (signInError.message.includes("Email not confirmed")) {
            // Afficher l'écran de confirmation email au lieu d'une erreur
            setEmailNotConfirmed(formData.email);
            setLoading(false);
            return;
          } else if (signInError.message.includes("Invalid login credentials")) {
            // Afficher l'écran dédié "compte introuvable ou mot de passe incorrect"
            setLoginFailed(formData.email);
            setLoading(false);
            return;
          } else {
            // Traduire les erreurs Supabase courantes
            let errorMessage = signInError.message;
            if (signInError.message.includes("rate limit") || signInError.message.includes("too many")) {
              errorMessage = "Trop de tentatives. Veuillez patienter quelques minutes avant de réessayer.";
            }
            toast({
              title: "Erreur de connexion",
              description: errorMessage,
              variant: "destructive",
            });
          }
          setLoading(false);
          return;
        }

        toast({
          title: "Connexion réussie",
          description: "Bienvenue sur B8ild !",
        });
        // Si l'onboarding n'a pas été complété, rediriger vers l'onboarding
        if (!checkOnboardingCompleted()) {
          navigate("/onboarding");
        } else {
          // Vérifier s'il y a une URL de retour (après session expirée)
          const returnUrl = getAndClearReturnUrl();
          navigate(returnUrl || "/dashboard");
        }
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur inattendue s'est produite.",
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  // Fonction pour demander un reset de mot de passe
  const handleForgotPassword = async () => {
    if (!formData.email) {
      setErrors({ email: "Entrez votre email pour réinitialiser le mot de passe" });
      return;
    }

    const emailValidation = z.string().email().safeParse(formData.email);
    if (!emailValidation.success) {
      setErrors({ email: "Email invalide" });
      return;
    }

    setLoading(true);
    const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin;
    const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
      redirectTo: `${baseUrl}/reset-password`,
    });
    setLoading(false);

    if (error) {
      // Log pour debug
      if (import.meta.env.DEV) console.log('[Auth] Reset password error:', error.message, error.status);

      // Détection rate limit - Supabase peut retourner différents formats
      const isRateLimit =
        error.message.toLowerCase().includes("rate limit") ||
        error.message.toLowerCase().includes("too many") ||
        error.message.includes("429") ||
        error.message.toLowerCase().includes("email rate limit") ||
        error.status === 429;

      if (isRateLimit) {
        setRateLimitError(true);
      } else {
        toast({
          title: "Erreur",
          description: error.message,
          variant: "destructive",
        });
      }
    } else {
      setResetEmailSent(formData.email);
    }
  };

  // Fonction pour renvoyer l'email de confirmation
  const handleResendConfirmation = async () => {
    if (!emailNotConfirmed) return;
    setLoading(true);
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: emailNotConfirmed,
    });
    setLoading(false);
    if (error) {
      // Traduire les erreurs Supabase courantes
      let errorMessage = error.message;
      if (error.message.includes("rate limit") || error.message.includes("too many")) {
        errorMessage = "Trop de demandes. Veuillez patienter quelques minutes avant de réessayer.";
      } else if (error.message.includes("not found")) {
        errorMessage = "Aucun compte trouvé avec cet email.";
      }
      toast({
        title: "Impossible d'envoyer l'email",
        description: errorMessage,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Email envoyé !",
        description: "Vérifiez votre boîte de réception.",
      });
    }
  };

  // Fonction pour se connecter avec Google
  const handleGoogleSignIn = async () => {
    // En mode signup, exiger le nom de l'entreprise
    if (mode === "signup") {
      if (!formData.nomEntreprise || formData.nomEntreprise.trim().length < 2) {
        setErrors({ nomEntreprise: "Le nom de l'entreprise est requis pour créer un compte" });
        toast({
          title: "Information requise",
          description: "Veuillez entrer le nom de votre entreprise avant de continuer avec Google.",
          variant: "destructive",
        });
        return;
      }
      // Stocker le nom de l'entreprise pour le récupérer après OAuth
      localStorage.setItem('pendingEntreprise', formData.nomEntreprise.trim());
    }

    setGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Google sign in error:', error);
    } finally {
      setGoogleLoading(false);
    }
  };

  // Écran dédié pour erreur de rate limit
  if (rateLimitError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0F] px-4 py-12 relative overflow-hidden">
        {/* Gradient mesh background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0F] via-[#0F172A] to-[#0A0A0F]" />
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#F59E0B]/8 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#1A365D]/30 rounded-full blur-[120px]" />
        </div>

        {/* Subtle grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:64px_64px]" />

        {/* Card with gradient border */}
        <div className="relative z-10 w-full max-w-md animate-fade-up">
          <div className="rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.15] to-white/[0.02]">
            <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl p-8">
              {/* Header */}
              <div className="text-center space-y-4 mb-6">
                <div className="mx-auto p-4 rounded-full bg-orange-500/10 w-fit">
                  <Clock className="h-10 w-10 text-orange-400" />
                </div>
                <h1 className="text-2xl font-bold text-white">Trop de demandes</h1>
                <p className="text-white/50">
                  Vous avez effectué trop de demandes.<br />
                  Veuillez patienter quelques minutes avant de réessayer.
                </p>
              </div>

              {/* Content */}
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.05] text-sm text-white/60 space-y-2">
                  <p>Cette limite protège votre compte contre les abus.</p>
                  <p>Attendez <strong className="text-white/80">2-3 minutes</strong> puis réessayez.</p>
                </div>
                <Button
                  onClick={() => setRateLimitError(false)}
                  className="w-full h-12 font-semibold bg-[#F59E0B] hover:bg-[#FBBF24] text-[#0A0A0F]"
                >
                  Retour à la connexion
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Écran dédié pour email de reset envoyé
  if (resetEmailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0F] px-4 py-12 relative overflow-hidden">
        {/* Gradient mesh background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0F] via-[#0F172A] to-[#0A0A0F]" />
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#F59E0B]/8 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#1A365D]/30 rounded-full blur-[120px]" />
        </div>

        {/* Subtle grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:64px_64px]" />

        {/* Card with gradient border */}
        <div className="relative z-10 w-full max-w-md animate-fade-up">
          <div className="rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.15] to-white/[0.02]">
            <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl p-8">
              {/* Header */}
              <div className="text-center space-y-4 mb-6">
                <div className="mx-auto p-4 rounded-full bg-[#F59E0B]/10 w-fit">
                  <Mail className="h-10 w-10 text-[#F59E0B]" />
                </div>
                <h1 className="text-2xl font-bold text-white">Email envoyé !</h1>
                <p className="text-white/50">
                  Si un compte existe pour<br />
                  <strong className="text-white">{resetEmailSent}</strong><br />
                  vous recevrez un lien de réinitialisation.
                </p>
              </div>

              {/* Content */}
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.05] text-sm text-white/60 space-y-2">
                  <p>Cliquez sur le lien dans l'email pour choisir un nouveau mot de passe.</p>
                  <p>Pensez à vérifier vos <strong className="text-white/80">spams</strong> si vous ne trouvez pas l'email.</p>
                </div>
                <Button
                  onClick={() => {
                    setResetEmailSent(null);
                    setForgotPasswordMode(false);
                  }}
                  className="w-full h-12 font-semibold bg-[#F59E0B] hover:bg-[#FBBF24] text-[#0A0A0F]"
                >
                  Retour à la connexion
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Écran dédié pour email non confirmé
  if (emailNotConfirmed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0F] px-4 py-12 relative overflow-hidden">
        {/* Gradient mesh background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0F] via-[#0F172A] to-[#0A0A0F]" />
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#F59E0B]/8 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#1A365D]/30 rounded-full blur-[120px]" />
        </div>

        {/* Subtle grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:64px_64px]" />

        {/* Card with gradient border */}
        <div className="relative z-10 w-full max-w-md animate-fade-up">
          <div className="rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.15] to-white/[0.02]">
            <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl p-8">
              {/* Header */}
              <div className="text-center space-y-4 mb-6">
                <div className="mx-auto p-4 rounded-full bg-[#F59E0B]/10 w-fit">
                  <Mail className="h-10 w-10 text-[#F59E0B]" />
                </div>
                <h1 className="text-2xl font-bold text-white">Vérifiez votre email</h1>
                <p className="text-white/50">
                  Nous avons envoyé un lien de confirmation à<br />
                  <strong className="text-white">{emailNotConfirmed}</strong>
                </p>
              </div>

              {/* Content */}
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.05] text-sm text-white/60 space-y-2">
                  <p>Cliquez sur le lien dans l'email pour activer votre compte.</p>
                  <p>Pensez à vérifier vos <strong className="text-white/80">spams</strong> si vous ne trouvez pas l'email.</p>
                </div>
                <Button
                  onClick={handleResendConfirmation}
                  variant="outline"
                  className="w-full font-semibold border-white/20 text-white hover:bg-white/[0.05] hover:border-[#F59E0B]/50"
                  disabled={loading}
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Renvoyer l'email de confirmation
                </Button>
                <button
                  onClick={() => setEmailNotConfirmed(null)}
                  className="w-full text-sm text-white/40 hover:text-white transition-colors py-2"
                >
                  ← Retour à la connexion
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Écran dédié pour erreur de connexion (email/mot de passe incorrect)
  if (loginFailed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0F] px-4 py-12 relative overflow-hidden">
        {/* Gradient mesh background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0F] via-[#0F172A] to-[#0A0A0F]" />
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#F59E0B]/8 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#1A365D]/30 rounded-full blur-[120px]" />
        </div>

        {/* Subtle grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:64px_64px]" />

        {/* Card with gradient border */}
        <div className="relative z-10 w-full max-w-md animate-fade-up">
          <div className="rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.15] to-white/[0.02]">
            <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl p-8">
              {/* Header */}
              <div className="text-center space-y-4 mb-6">
                <div className="mx-auto p-4 rounded-full bg-red-500/10 w-fit">
                  <AlertCircle className="h-10 w-10 text-red-400" />
                </div>
                <h1 className="text-2xl font-bold text-white">Connexion impossible</h1>
                <p className="text-white/50">
                  Aucun compte trouvé pour<br />
                  <strong className="text-white">{loginFailed}</strong>
                </p>
              </div>

              {/* Content */}
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.05] text-sm text-white/60">
                  <p>Soit cet email n'est pas inscrit sur B8ild, soit le mot de passe est incorrect.</p>
                </div>
                <Button
                  onClick={() => {
                    setLoginFailed(null);
                    setMode("signup");
                    setFormData({ ...formData, email: loginFailed });
                  }}
                  className="w-full h-12 font-semibold bg-[#F59E0B] hover:bg-[#FBBF24] text-[#0A0A0F]"
                >
                  Créer un compte avec cet email
                </Button>
                <Button
                  onClick={() => setLoginFailed(null)}
                  variant="outline"
                  className="w-full font-semibold border-white/20 text-white hover:bg-white/[0.05] hover:border-[#F59E0B]/50"
                >
                  Réessayer avec un autre mot de passe
                </Button>
                <button
                  onClick={() => {
                    setLoginFailed(null);
                    setFormData({ ...formData, email: "", password: "" });
                  }}
                  className="w-full text-sm text-white/40 hover:text-white transition-colors py-2"
                >
                  ← Utiliser un autre email
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0F] px-4 py-12 relative overflow-hidden">
      {/* Gradient mesh background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0F] via-[#0F172A] to-[#0A0A0F]" />
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#F59E0B]/8 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#1A365D]/30 rounded-full blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-[#F59E0B]/5 to-transparent rounded-full" />
      </div>

      {/* Subtle grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:64px_64px]" />

      {/* Card with gradient border */}
      <div className="relative z-10 w-full max-w-md animate-fade-up">
        <div className="rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.15] to-white/[0.02]">
          <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl p-8">
            {/* Header */}
            <div className="space-y-4 mb-8">
              <Link to="/" className="flex items-center">
                <span className="text-2xl font-bold text-white tracking-tight">
                  B<span className="text-[#F59E0B]">8</span>ild
                </span>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-white">
                  {mode === "login" ? "Bon retour !" : "Créer un compte"}
                </h1>
                <p className="text-white/50 mt-1">
                  {mode === "login"
                    ? "Connectez-vous pour accéder à votre tableau de bord"
                    : "Commencez votre essai gratuit de 14 jours"}
                </p>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {mode === "signup" && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="prenom" className="text-sm font-medium text-white/70">Prénom</Label>
                      <Input
                        id="prenom"
                        name="prenom"
                        type="text"
                        value={formData.prenom}
                        onChange={handleChange}
                        disabled={loading}
                        className={`bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/30 focus:border-[#F59E0B]/50 focus:ring-[#F59E0B]/20 ${errors.prenom ? "border-red-500" : ""}`}
                      />
                      {errors.prenom && (
                        <InlineError message={errors.prenom} className="mt-1" />
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nom" className="text-sm font-medium text-white/70">Nom</Label>
                      <Input
                        id="nom"
                        name="nom"
                        type="text"
                        value={formData.nom}
                        onChange={handleChange}
                        disabled={loading}
                        className={`bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/30 focus:border-[#F59E0B]/50 focus:ring-[#F59E0B]/20 ${errors.nom ? "border-red-500" : ""}`}
                      />
                      {errors.nom && (
                        <InlineError message={errors.nom} className="mt-1" />
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="nomEntreprise" className="text-sm font-medium text-white/70">Nom de l'entreprise <span className="text-red-400">*</span></Label>
                    <Input
                      id="nomEntreprise"
                      name="nomEntreprise"
                      type="text"
                      value={formData.nomEntreprise}
                      onChange={handleChange}
                      disabled={loading}
                      className={`bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/30 focus:border-[#F59E0B]/50 focus:ring-[#F59E0B]/20 ${errors.nomEntreprise ? "border-red-500" : ""}`}
                    />
                    {errors.nomEntreprise && (
                      <InlineError message={errors.nomEntreprise} className="mt-1" />
                    )}
                  </div>
                </>
              )}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-white/70">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="vous@exemple.com"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                  className={`bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/30 focus:border-[#F59E0B]/50 focus:ring-[#F59E0B]/20 ${errors.email ? "border-red-500" : ""}`}
                />
                {errors.email && (
                  <InlineError message={errors.email} className="mt-1" />
                )}
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium text-white/70">Mot de passe</Label>
                  {mode === "login" && (
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      className="text-xs text-[#F59E0B] hover:text-[#FBBF24] transition-colors"
                      disabled={loading}
                    >
                      Mot de passe oublié ?
                    </button>
                  )}
                </div>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                  className={`bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/30 focus:border-[#F59E0B]/50 focus:ring-[#F59E0B]/20 ${errors.password ? "border-red-500" : ""}`}
                />
                {errors.password && (
                  <InlineError message={errors.password} className="mt-1" />
                )}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full h-12 bg-[#F59E0B] hover:bg-[#FBBF24] text-[#0A0A0F] font-semibold text-base rounded-xl shadow-[0_0_0_1px_rgba(245,158,11,0.3),0_8px_40px_rgba(245,158,11,0.3)] hover:shadow-[0_0_0_1px_rgba(245,158,11,0.5),0_12px_50px_rgba(245,158,11,0.4)] hover:scale-[1.02] transition-all duration-300"
                disabled={loading}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {mode === "login" ? "Se connecter" : "Créer mon compte"}
              </Button>

              {/* Séparateur */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-[#0F0F14] px-2 text-white/40">Ou</span>
                </div>
              </div>

              {/* Bouton Google */}
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 font-semibold bg-transparent border-white/20 text-white hover:bg-white/[0.05] hover:border-[#F59E0B]/50"
                onClick={handleGoogleSignIn}
                disabled={googleLoading || loading}
              >
                {googleLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                )}
                Continuer avec Google
              </Button>

              {/* Toggle mode */}
              <div className="text-sm text-center text-white/40 pt-2">
                {mode === "login" ? (
                  <>
                    Pas encore de compte ?{" "}
                    <button
                      type="button"
                      onClick={() => setMode("signup")}
                      className="text-[#F59E0B] hover:text-[#FBBF24] font-semibold transition-colors"
                      disabled={loading}
                    >
                      Créer un compte
                    </button>
                  </>
                ) : (
                  <>
                    Déjà un compte ?{" "}
                    <button
                      type="button"
                      onClick={() => setMode("login")}
                      className="text-[#F59E0B] hover:text-[#FBBF24] font-semibold transition-colors"
                      disabled={loading}
                    >
                      Se connecter
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
